import { useEffect } from "react";
import { useIntl } from "react-intl";
import { TOAST_VARIANTS, TOAST_POSITIONS } from "@cdkglobal/radial";
import Toaster from "../../../common/Toaster";
import { useUIStateContext } from "../../../../contexts/UIStateContext";
import { getARMigrationInfo } from "../../helpers";
import { RowData } from "../../../../types";
import { DEFAULT_TOAST } from "../../constants";

interface ARMigrationToastProps {
  dataToDisplay: RowData[];
}

export default function ARMigrationToast({
  dataToDisplay,
}: Readonly<ARMigrationToastProps>): JSX.Element | null {
  const {
    activeAccountsReceivableRecordMigrationInfo: { gridTableToast },
    setActiveAccountsReceivableRecordMigrationInfo,
  } = useUIStateContext();

  const intl = useIntl();

  useEffect(() => {
    const activeMigrationInfo = getARMigrationInfo(dataToDisplay);

    setActiveAccountsReceivableRecordMigrationInfo((prevState) => {
      if (!activeMigrationInfo) {
        return prevState.gridTableToast.message?.length
          ? { ...prevState, gridTableToast: DEFAULT_TOAST }
          : prevState;
      }

      if (prevState.gridTableToast.message === activeMigrationInfo.tableMessage) {
        return prevState;
      }

      return {
        ...prevState,
        gridTableToast: {
          message: activeMigrationInfo.tableMessage,
          variant: TOAST_VARIANTS.WARNING,
        },
      };
    });
  }, [dataToDisplay, setActiveAccountsReceivableRecordMigrationInfo]);

  const clearGridTableMessage = () => {
    setActiveAccountsReceivableRecordMigrationInfo((prevState) => ({
      ...prevState,
      gridTableToast: DEFAULT_TOAST,
    }));
  };

  if (!gridTableToast.message?.length) {
    return null;
  }

  return (
    <Toaster
      variant={gridTableToast.variant}
      message={gridTableToast.message}
      clearMessage={clearGridTableMessage}
      position={TOAST_POSITIONS.INLINE}
      actionText={intl.formatMessage({
        id: "action.dismiss",
        defaultMessage: "Dismiss",
      })}
      noActionButton={
        gridTableToast.variant === TOAST_VARIANTS.NEUTRAL ||
        gridTableToast.variant === TOAST_VARIANTS.WARNING
      }
      style={{ paddingBottom: "0.5rem", zIndex: 0 }}
    />
  );
}
